import React, {
	Dispatch,
	Fragment,
	SetStateAction,
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import type { IChat, TMessage, TMessagesByDate } from "@dreamcam/messenger-sdk";
import { Map as ImmutableMap, Set as ImmutableSet, OrderedMap, OrderedSet } from "immutable";
import type { Set } from "immutable";

import { Image } from "@components/Image";
import { onToast } from "@components/Toast";
import { Typography } from "@components/Typography";

import { useLayoutBreakpoint } from "@hooks/useLayoutBreakpoint";

import { useMessengerStore } from "@storage/global/useMessengerStore";

import { sdkMessenger } from "@services/messenger-sdk";

import { cn } from "@lib/utils";

import {
	DELAY_IMMEDIATE,
	DELAY_TIME_100,
	DELAY_TIME_500,
	DELAY_TIME_3000,
	SKELETON_BLOCK_CLASS,
} from "@configs/constants";
import { ICON_PATHS } from "@configs/icons";

import { ChatBlockPanel } from "./ChatBlockPanel";
import { ChatHeader } from "./ChatHeader";
import { GoDownBtn } from "./GoDownBtn";
import { MessageList } from "./MessageList";
import { SendMessageElement } from "./SendMessageElement";
import { debounce, readPreviousMessagesUntilTarget } from "./helpers";

const THRESHOLD = 0;

const ChatMessagesSkeleton = () => (
	<div className="flex h-full flex-col justify-end gap-2 py-2" aria-busy="true" aria-label="Loading messages">
		<div className={cn(SKELETON_BLOCK_CLASS, "mx-auto mb-2 h-3 w-16")} />
		<div className={cn(SKELETON_BLOCK_CLASS, "h-8 w-3/5 rounded-[20px]")} />
		<div className={cn(SKELETON_BLOCK_CLASS, "ml-auto h-12 w-4/5 rounded-[20px]")} />
		<div className={cn(SKELETON_BLOCK_CLASS, "h-16 w-4/5 rounded-[20px]")} />
		<div className={cn(SKELETON_BLOCK_CLASS, "ml-auto h-8 w-1/2 rounded-[20px]")} />
		<div className={cn(SKELETON_BLOCK_CLASS, "h-12 w-2/3 rounded-[20px]")} />
	</div>
);

interface ChatContainerProps {
	messageListByDate: TMessagesByDate;
	sendMessageAsync: (value: string) => Promise<void>;
	activeChat: string;
	throttleUpdateDraftMessageAsync: (chatId: string, message: string) => void;
	onStartedTypingThrottle: (chatId: string | null, isMessageWasSent: boolean) => void;
	getAllMessagesAsync: (
		chatId: string,
		isMore: boolean,
		relatedId?: string | null,
		position?: "forward" | "backward",
	) => Promise<void>;
	isLoaderMessages: boolean;
	isMoreMessagesMap: ImmutableMap<string, Record<"up" | "down", boolean>>;
	textInputMessage: string;
	chatInfo: IChat | null;
	isSendMessageError: boolean;
	cacheScrollPosition: ImmutableMap<string, number | string>;
	setCacheScrollPosition: Dispatch<SetStateAction<ImmutableMap<string, number | string>>>;
	setActivePanel: (isOpenChatList: boolean, currentChat: string | null) => void;
	isShowGoDownButton: boolean;
	setIsShowGoDownButton: Dispatch<SetStateAction<boolean>>;
	isLoadingChat: boolean;
	typingStatusList: Set<string>;
}

export const ChatContainer = memo(
	({
		messageListByDate,
		sendMessageAsync,
		activeChat,
		throttleUpdateDraftMessageAsync,
		onStartedTypingThrottle,
		getAllMessagesAsync,
		isLoaderMessages,
		isMoreMessagesMap,
		textInputMessage,
		chatInfo,
		isSendMessageError,
		cacheScrollPosition,
		setCacheScrollPosition,
		setActivePanel,
		isShowGoDownButton,
		setIsShowGoDownButton,
		isLoadingChat,
		typingStatusList,
	}: ChatContainerProps) => {
		const [sentMessagesMap, setSentMessagesMap] = useState<TSentMessages>(ImmutableMap());
		const [isFirstScrollToLastReadMessage, setIsFirstScrollToLastReadMessage] = useState<boolean>(false);
		const [isShowContent, setIsShowContent] = useState<boolean>(false);
		const [isLoadingLoadMoreUp, setIsLoadingLoadMoreUp] = useState<boolean>(false);
		const [isShowDateSticky, setIsShowDateSticky] = useState<boolean>(false);
		const [, setUpdatedShowDateStickyTimeout] = useState<NodeJS.Timeout | null>(null);
		const [isScrollToSavedPosition, setIsScrollToSavedPosition] = useState<boolean>(false);

		const { userMsgData, isOpenMessenger } = useMessengerStore();
		const sdk = sdkMessenger();
		const chatContainerRef = useRef<HTMLDivElement>(null);
		const observerLoadMessagesDown = useRef<IntersectionObserver | null>(null);
		const observerReadMessage = useRef<IntersectionObserver | null>(null);
		const messageCompanionRefs = useRef<TMessageRefs>(OrderedMap());
		const topElementRef = useRef<HTMLDivElement>(null);
		const { messengerUnsupportedByUser, isMessagesBoxFolded, setIsMessagesBoxFolded } = useMessengerStore();
		const messageBoxFormRef = useRef<HTMLDivElement>(null);
		const { MOBILE, TABLET } = useLayoutBreakpoint();
		const isMobile = MOBILE || TABLET;

		const findFirstUnreadMessageId = useCallback(() => {
			let findMessage = "";

			messageListByDate.some((itemMap) => {
				const element = itemMap.find(
					(item) => item["read_at"] === null && item["sender_id"] !== userMsgData.chat_client_id,
				);
				if (element) {
					findMessage = element.id;
					return true;
				}
				return false;
			});

			return findMessage;
		}, [messageListByDate]);

		const firstUnreadMessageIdMemo: string = useMemo(
			() => findFirstUnreadMessageId(),
			[activeChat, findFirstUnreadMessageId],
		);

		const lastMessageRef: React.RefCallback<Element> = useCallback(
			(node: Element) => {
				if (isLoaderMessages) return;
				if (observerLoadMessagesDown.current) observerLoadMessagesDown.current.disconnect();
				observerLoadMessagesDown.current = new IntersectionObserver(
					(entries) => {
						if (entries[THRESHOLD].isIntersecting && isMoreMessagesMap.get(activeChat)?.down) {
							if (node) {
								const targetMessageId = findLastMessageId();
								getAllMessagesAsync(activeChat, true, targetMessageId, "forward");
							}
						}
					},
					{
						root: chatContainerRef.current,
						threshold: 0.75,
					},
				);
				if (node) observerLoadMessagesDown.current.observe(node);
			},
			[isLoaderMessages, isMoreMessagesMap, messageListByDate],
		);

		const findLastMessageId = useCallback(() => {
			const getLastIndex = messageListByDate.valueSeq().last();
			if (getLastIndex) {
				const findFirstId = getLastIndex.last();
				if (findFirstId) {
					return findFirstId.id;
				}
			}

			return "";
		}, [messageListByDate]);

		const findFirstMessageId = useCallback(() => {
			const getFirstIndex = messageListByDate.valueSeq().first();
			if (getFirstIndex) {
				const findFirstId = getFirstIndex.first();
				if (findFirstId) {
					return findFirstId.id;
				}
			}

			return "";
		}, [messageListByDate]);

		const handleScroll = useCallback(() => {
			if (chatContainerRef.current) {
				const isBottom =
					chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop - 40 <=
					chatContainerRef.current.clientHeight;
				setIsShowGoDownButton(!isBottom);

				if (isFirstScrollToLastReadMessage && activeChat !== null) {
					const condition = isBottom
						? "bottom"
						: chatContainerRef.current.scrollTop === THRESHOLD
							? 1
							: chatContainerRef.current.scrollTop;
					const updateCacheScrollPosition = cacheScrollPosition.set(activeChat, condition);
					setCacheScrollPosition(updateCacheScrollPosition);
				}
			}
		}, [isFirstScrollToLastReadMessage, activeChat]);

		const findLastUnreadMessage = useCallback(
			(isToBottom: boolean, firstUnreadMessageId: string) => {
				if (activeChat === null) return;
				const getAllElements = messageCompanionRefs.current;
				const getCacheScrollPosition = cacheScrollPosition.get(activeChat);

				if (
					getCacheScrollPosition &&
					typeof getCacheScrollPosition === "number" &&
					!isScrollToSavedPosition &&
					!isShowContent
				) {
					if (chatContainerRef.current) {
						chatContainerRef.current.scrollTop = getCacheScrollPosition;
						setIsScrollToSavedPosition((prev) => !prev);
					}
				} else if (firstUnreadMessageId) {
					const findElement = getAllElements.get(firstUnreadMessageId);
					if (findElement && findElement.data.read_at === null) {
						findElement.element.scrollIntoView({ behavior: isToBottom ? "smooth" : "instant", block: "start" });
					} else {
						scrollToBottom(isToBottom);
					}
				} else {
					scrollToBottom(isToBottom);
				}

				setTimeout(() => {
					setIsFirstScrollToLastReadMessage(true);
					setIsShowContent(true);
				}, DELAY_TIME_100);
			},
			[activeChat, messageCompanionRefs, isScrollToSavedPosition, cacheScrollPosition],
		);

		const handleReadMessages = useCallback(
			debounce(async (messageId: string, sentMessagesMap: TSentMessages) => {
				if (isMessagesBoxFolded) return;

				const findChatSentMessages = sentMessagesMap.get(activeChat);
				const isMessageSent = findChatSentMessages && findChatSentMessages.has(messageId);
				const findMessageData = messageCompanionRefs.current.get(messageId);

				if (findMessageData) {
					if (messageId && !isMessageSent && findMessageData.data.read_at === null) {
						try {
							readPreviousMessagesUntilTarget(messageId, messageCompanionRefs, setSentMessagesMap, activeChat);
							await sdk.chatManager.readMessageAsync(activeChat, messageId);
						} catch (error) {
							const errorMessage = (error as Error).message;
							onToast({
								message: "Error",
								description: errorMessage,
							});
						}
					}
				}
			}, DELAY_TIME_500),
			[activeChat, isMessagesBoxFolded],
		);

		const observeVisibleElements = useCallback(
			debounce(() => {
				if (observerReadMessage.current) observerReadMessage.current.disconnect();
				let visibleMessages: OrderedSet<Element> = OrderedSet();
				observerReadMessage.current = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => {
							if (entry.isIntersecting) {
								visibleMessages = visibleMessages.add(entry.target);
							} else {
								visibleMessages = visibleMessages.delete(entry.target);
							}
						});

						if (visibleMessages.size > THRESHOLD) {
							let messageId: string = "";
							const getEntries = messageCompanionRefs.current.entrySeq();
							for (const [key, value] of getEntries) {
								if (value.element === visibleMessages.last()) {
									messageId = key;
								}
							}
							if (messageId) {
								handleReadMessages(messageId, sentMessagesMap);
							}
						}
					},
					{
						root: chatContainerRef.current,
						// threshold: 0.1,
					},
				);

				if (messageCompanionRefs.current.size === THRESHOLD) return;
				messageCompanionRefs.current.forEach((message) => {
					if (message.element && observerReadMessage.current) {
						observerReadMessage.current.observe(message.element);
					}
				});
			}, 500),
			[handleReadMessages, messageCompanionRefs.current, sentMessagesMap],
		);

		const scrollToElement = useCallback(() => {
			if (chatContainerRef.current) {
				const findElem = findFirstUnreadMessageId();
				findLastUnreadMessage(true, findElem);
				chatContainerRef.current.style.scrollBehavior = "auto";
			}
		}, [isScrollToSavedPosition, findFirstUnreadMessageId]);

		const scrollToBottom = useCallback(
			(isToBottom: boolean) => {
				if (chatContainerRef.current) {
					chatContainerRef.current.style.scrollBehavior = isToBottom ? "smooth" : "auto";
					chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
				}
			},
			[isScrollToSavedPosition],
		);

		const updateUnreadMessages = useMemo(() => {
			if (chatInfo === null) return THRESHOLD;
			return chatInfo?.unread_count;
		}, [chatInfo]);

		const isBannedCompanion: boolean = useMemo(() => {
			if (chatInfo === null) return false;

			return chatInfo?.participant.block_data.is_blocked;
		}, [chatInfo]);

		const isMutedCompanion: boolean = useMemo(() => {
			if (chatInfo === null) return false;

			return chatInfo?.participant.mute_data.is_muted;
		}, [chatInfo]);

		const isCompanionSupportedMessenger: boolean = useMemo(() => {
			if (chatInfo === null) return false;

			return chatInfo?.participant.messenger_supported;
		}, [chatInfo]);

		useEffect(() => {
			setSentMessagesMap((prev) => prev.set(activeChat, ImmutableSet()));

			return () => {
				messageCompanionRefs.current.clear();
			};
		}, [activeChat]);

		useEffect(() => {
			if (chatContainerRef.current) {
				observeVisibleElements();

				chatContainerRef.current.addEventListener("scroll", () => {
					handleScroll();

					if (isShowContent) {
						setIsShowDateSticky(true);
					}
				});

				chatContainerRef.current.addEventListener("scrollend", () => {
					setUpdatedShowDateStickyTimeout((prev) => {
						if (prev) {
							clearTimeout(prev);
						}

						const newTimeout = setTimeout(() => {
							setIsShowDateSticky(false);
						}, DELAY_TIME_3000);

						prev = newTimeout;
						return prev;
					});
				});
			}

			return () => {
				if (chatContainerRef.current) {
					chatContainerRef.current.removeEventListener("scroll", () => {
						handleScroll();
						setIsShowContent(true);
					});
				}
				if (observerReadMessage.current) {
					observerReadMessage.current.disconnect();
				}
			};
		}, [chatContainerRef, handleScroll, isFirstScrollToLastReadMessage, observeVisibleElements]);

		useEffect(() => {
			if (isLoadingLoadMoreUp) return;
			const observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						if (entry.target.id === "top" && isMoreMessagesMap.get(activeChat)?.up && !isLoaderMessages) {
							setIsLoadingLoadMoreUp(true);

							const targetMessageId = findFirstMessageId();
							if (!chatContainerRef.current) return;

							chatContainerRef.current.style.scrollBehavior = "auto";
							const prevScrollHeight = chatContainerRef.current.scrollHeight;
							const prevScrollTop = chatContainerRef.current.scrollTop;

							getAllMessagesAsync(activeChat, true, targetMessageId, "backward").then(() => {
								requestAnimationFrame(() => {
									if (!chatContainerRef.current) return;
									const newScrollHeight = chatContainerRef.current.scrollHeight;
									const heightDifference = newScrollHeight - prevScrollHeight;

									chatContainerRef.current.scrollTop = prevScrollTop + heightDifference;
								});
							});

							setIsLoadingLoadMoreUp(false);
						}
					}
				});
			});

			if (topElementRef.current) {
				observer.observe(topElementRef.current);
			}

			return () => {
				observer.disconnect();
			};
		}, [isMoreMessagesMap, isLoaderMessages, chatContainerRef.current]);

		useEffect(() => {
			if (
				messageListByDate.size > THRESHOLD &&
				!isFirstScrollToLastReadMessage &&
				messageCompanionRefs.current.size > THRESHOLD
			) {
				const findElem = findFirstUnreadMessageId();
				setTimeout(() => {
					findLastUnreadMessage(false, findElem);
				}, DELAY_IMMEDIATE);
			}
		}, [messageListByDate, isFirstScrollToLastReadMessage, messageCompanionRefs, findFirstUnreadMessageId]);

		useEffect(() => {
			if (isMobile) {
				setIsMessagesBoxFolded(false);
				return;
			}

			if (isOpenMessenger) {
				setIsMessagesBoxFolded(true);
			}
		}, [isMobile, isOpenMessenger, setIsMessagesBoxFolded]);

		useEffect(() => {
			if (!isMessagesBoxFolded) {
				handleScroll();
			}
		}, [isMessagesBoxFolded]);

		useLayoutEffect(() => {
			const vh = window.innerHeight * 0.01;
			document.documentElement.style.setProperty("--vh", `${vh}px`);

			window.addEventListener("resize", () => {
				const vh = window.innerHeight * 0.01;
				document.documentElement.style.setProperty("--vh", `${vh}px`);
			});
		}, []);

		return (
			<div
				className={cn(
					"z-docked flex shrink-0 flex-col items-center gap-2.5 rounded-xl bg-block",
					isMobile
						? "fixed inset-0 z-max h-[calc(var(--vh,1vh)*100)] w-screen transition-all duration-300 ease-[ease]"
						: "fixed right-[10%] bottom-0 h-118.5 w-90 shadow-[0px_4px_17.8px_0px_rgba(255,255,255,0.06)]",
				)}
				style={!isMobile && isMessagesBoxFolded ? { height: 50 } : {}}
			>
				<div className="flex h-full w-full flex-col rounded-tl-[10px] rounded-tr-[10px] rounded-br-[10px] bg-block">
					<ChatHeader
						categoryByUser={chatInfo?.participant.category}
						setActivePanel={setActivePanel}
						activeChat={activeChat}
						chatInfo={chatInfo}
						typingStatusList={typingStatusList}
					/>
					<div
						className={cn(
							"messages-box-scrollbar h-full overflow-auto px-5 pt-0 pb-4",
							(!isCompanionSupportedMessenger || messengerUnsupportedByUser.isDisabled) && "flex justify-center",
						)}
						ref={chatContainerRef}
						id="chat-container"
						style={isBannedCompanion || isMutedCompanion ? { opacity: 0.3 } : { opacity: 1 }}
					>
						{isCompanionSupportedMessenger && !messengerUnsupportedByUser.isDisabled ? (
							<>
								{messageListByDate.size > 0 ? (
									<div
										style={{
											width: "100%",
											position: "relative",
										}}
									>
										{messageListByDate
											.keySeq()
											.toArray()
											.map((date, index) => (
												<Fragment key={date}>
													<MessageList
														key={date}
														date={date}
														listMessages={messageListByDate.get(date)?.toArray() || []}
														sectionIndex={index}
														topElementRef={topElementRef}
														lastTargetIndex={findLastMessageId()}
														lastMessageRef={lastMessageRef}
														messageCompanionRefs={messageCompanionRefs}
														firstUnreadMessageId={firstUnreadMessageIdMemo}
														isLoadingLoadMoreUp={isLoadingLoadMoreUp}
														isShowDateSticky={isShowDateSticky}
													/>
												</Fragment>
											))}
									</div>
								) : isLoadingChat ? (
									<ChatMessagesSkeleton />
								) : (
									<div className="flex h-full flex-col items-center justify-center">
										<Typography type="span" size="3xl" weight="bold">
											No messages yet
										</Typography>
										<Typography type="span" color="msg_time">
											Start the conversation
										</Typography>
									</div>
								)}
							</>
						) : (
							<div className="flex flex-col items-center justify-center gap-4">
								<Image width={39} height={39} src={ICON_PATHS.DISABLED_REGION} alt="disabled_region" />
								<span className="max-w-43.5 text-center text-sm leading-normal font-bold text-white">
									Messaging is currently disabled for this user
								</span>
							</div>
						)}
					</div>

					{!isMessagesBoxFolded && !messengerUnsupportedByUser.isDisabled ? (
						<GoDownBtn
							isShow={isShowContent ? isShowGoDownButton : false}
							amount={updateUnreadMessages}
							scrollToElement={scrollToElement}
							isBlocked={isBannedCompanion || isMutedCompanion}
							messageBoxFormRef={messageBoxFormRef}
						/>
					) : null}

					{isBannedCompanion || isMutedCompanion ? (
						<ChatBlockPanel userId={chatInfo?.participant?.user_id || ""} isMuted={isMutedCompanion} />
					) : (
						<>
							{isCompanionSupportedMessenger && !messengerUnsupportedByUser.isDisabled ? (
								<div className={cn("mt-auto", isMobile && "h-28.75 w-full bg-block")} ref={messageBoxFormRef}>
									<SendMessageElement
										sendMessageAsync={sendMessageAsync}
										throttleUpdateDraftMessageAsync={throttleUpdateDraftMessageAsync}
										onStartedTypingThrottle={onStartedTypingThrottle}
										activeChat={activeChat}
										textInputMessage={textInputMessage}
										isSendMessageError={isSendMessageError}
										isNewChat={messageListByDate.size === 0}
									/>
								</div>
							) : null}
						</>
					)}
				</div>
			</div>
		);
	},
);

export type TMessageRefs = OrderedMap<string, TRefElementValue>;
export type TRefElementValue = { element: HTMLDivElement; data: TMessage };
export type TSentMessages = ImmutableMap<string, ImmutableSet<string>>;
